import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, ArrowUpRight, CheckCircle2, CircleDot, Circle } from "lucide-react";
import api from "@/lib/api";
import { Overline, FadeIn, StatusBadge } from "@/components/site/Shared";

const milestoneIcon = (status) => {
  if (status === "completed") return <CheckCircle2 size={20} className="text-emerald-400" />;
  if (status === "in_progress") return <CircleDot size={20} className="text-ember" />;
  return <Circle size={20} className="text-silver/50" />;
};

export default function ProjectDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setP(null);
    api.get(`/projects/${slug}`).then(({ data }) => setP(data)).catch(() => setP(false));
  }, [slug]);

  if (p === null) return <div className="min-h-[60vh] flex items-center justify-center text-silver uppercase tracking-widest text-sm">Loading…</div>;
  if (p === false) return <div className="min-h-[60vh] flex items-center justify-center text-silver">Project not found.</div>;

  const facts = [
    ["Location", p.location], ["Category", p.category], ["Client", p.client_name || "Confidential"],
    ["Built-Up Area", p.area], ["Duration", p.duration], ["Budget Range", p.budget_range || "On request"],
    ["Completion", `${p.completion_pct}%`], ["Year", p.year],
  ];

  return (
    <div data-testid="project-detail-page">
      <section className="relative border-b border-line overflow-hidden">
        <div className="absolute inset-0">
          <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-carbon via-carbon/60 to-carbon/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-40 pb-16">
          <FadeIn>
            <div className="flex items-center gap-4 mb-4">
              <StatusBadge status={p.status} />
              <span className="text-silver text-xs uppercase tracking-[0.2em]">{p.tracking_code}</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter text-white leading-[0.95]">{p.title}</h1>
            <p className="text-silver mt-4 flex items-center gap-2"><MapPin size={16} className="text-ember" /> {p.location}</p>
          </FadeIn>
        </div>
      </section>

      <section className="border-b border-line bg-graphite">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 divide-x divide-line border-x border-line" data-testid="project-facts">
          {facts.map(([k, v]) => (
            <div key={k} className="p-5 border-b lg:border-b-0 border-line">
              <p className="text-silver text-[10px] uppercase tracking-[0.18em]">{k}</p>
              <p className="text-white text-sm font-medium mt-1.5 leading-snug">{v}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-16">
          <div className="lg:col-span-3">
            <FadeIn>
              <Overline className="mb-4">The Project</Overline>
              <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight text-white">{p.summary}</h2>
              <p className="text-silver mt-6 leading-relaxed">{p.description}</p>

              <h3 className="font-heading text-xl uppercase tracking-tight text-white mt-12 mb-6">Technical Highlights</h3>
              <div className="grid sm:grid-cols-2 gap-px bg-line border border-line">
                {p.technical?.map((t) => (
                  <div key={t} className="bg-carbon px-6 py-5 text-white/90 text-sm flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-ember mt-1.5 shrink-0" /> {t}
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>

          <div className="lg:col-span-2">
            <FadeIn delay={0.1}>
              <Overline className="mb-4">Progress Timeline</Overline>
              <div className="border border-line bg-graphite p-8" data-testid="project-milestones">
                <div className="flex justify-between text-xs text-silver mb-2 uppercase tracking-wider">
                  <span>Overall Completion</span><span className="text-ember font-bold">{p.completion_pct}%</span>
                </div>
                <div className="h-1.5 bg-line mb-10"><div className="h-1.5 bg-ember" style={{ width: `${p.completion_pct}%` }} /></div>
                <div className="space-y-0 border-l border-line ml-2.5">
                  {p.milestones?.map((m, i) => (
                    <div key={i} className="relative pl-9 pb-8 last:pb-0">
                      <span className="absolute -left-[10px] top-0 bg-graphite">{milestoneIcon(m.status)}</span>
                      <p className={`text-sm font-medium ${m.status === "pending" ? "text-silver" : "text-white"}`}>{m.title}</p>
                      <p className="text-silver/70 text-xs mt-1 uppercase tracking-wider">{m.date}</p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="py-24 border-b border-line bg-graphite">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Overline className="mb-4">Gallery</Overline>
          <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight text-white mb-10">On-Site & As Delivered</h2>
          <div className="img-zoom overflow-hidden border border-line mb-4">
            <img src={p.gallery?.[active]} alt={`${p.title} view ${active + 1}`} className="w-full h-[420px] sm:h-[540px] object-cover" data-testid="gallery-main-image" />
          </div>
          <div className="grid grid-cols-4 gap-4">
            {p.gallery?.map((g, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
                className={`overflow-hidden border ${active === i ? "border-ember" : "border-line opacity-60 hover:opacity-100"} transition-opacity duration-200`}>
                <img src={g} alt={`thumbnail ${i + 1}`} className="w-full h-24 sm:h-32 object-cover" />
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-ember">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
          <h2 className="font-heading text-3xl sm:text-4xl uppercase tracking-tighter text-white">Want Results Like This?</h2>
          <Link to="/request-quote" data-testid="project-cta-btn" className="bg-carbon text-white uppercase text-sm tracking-[0.15em] font-bold px-8 py-4 flex items-center gap-2 hover:bg-black transition-colors duration-200">
            Request a Quote <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
